'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useRef } from 'react';
import { IconArrowUpRight } from '@/components/icons/IconArrowUpRight';
import { IconPlay } from '@/components/icons/IconPlay';
import { useMediaLightbox } from '@/features/media-lightbox';
import { BaseLink } from '@/features/navigation';
import { resolveImageActionLabel, resolveImageAlt, sanityImage } from '@/lib/sanity/image';
import { FULL_WIDTH_HINT, imageSizes } from '@/lib/utils/imageSizes';
import { stegaClean } from '@/lib/utils/stega';
import { cn } from '@/lib/utils/tw-merge';
import type { UiMediaCardProps } from './UiMediaCard.types';

/**
 * One editorial tile: media, title, one line of context.
 *
 * The `kind` is what keeps a feed from reading as a showcase — own work gets a
 * play affordance and a graded, filmic still; a social post gets the
 * leaving-the-site cue and a brighter, punchier one.
 *
 * A client component for one reason: the loop starts on hover and only then.
 * `preload="none"` means a grid of thirty tiles costs thirty poster images and
 * no video bytes at all until somebody actually points at one.
 */
function UiMediaCard({ card, feature = false, widthFraction = 1, area, cue = true, className }: UiMediaCardProps) {
    const kind = stegaClean(card.kind) === 'social' ? 'social' : 'project';
    const format = stegaClean(card.format) ?? 'landscape';
    const src = sanityImage.heroBackground(card.image);
    const videoUrl = card.video?.asset?.url;

    const videoRef = useRef<HTMLVideoElement>(null);
    const lightbox = useMediaLightbox();

    const startPreview = () => {
        void videoRef.current?.play().catch(() => {
            // Autoplay policies and a pointer that has already left again: both
            // end here, and both are fine — the still is still showing.
        });
    };

    const stopPreview = () => {
        const video = videoRef.current;
        if (!video) return;

        video.pause();
        video.currentTime = 0;
    };

    const media = (
        <>
            <span className="media-card__media">
                {src ? (
                    <Image
                        src={src}
                        alt={resolveImageAlt(card.image, card.title)}
                        fill
                        sizes={imageSizes(FULL_WIDTH_HINT, widthFraction)}
                        className="media-card__photo"
                    />
                ) : null}

                {videoUrl ? (
                    <video
                        ref={videoRef}
                        className="media-card__video"
                        muted
                        loop
                        playsInline
                        preload="none"
                        poster={src ?? undefined}
                        aria-hidden="true"
                        tabIndex={-1}
                    >
                        <source src={videoUrl} type={card.video?.asset?.mimeType ?? undefined} />
                    </video>
                ) : null}

                <span className="media-card__grain" aria-hidden="true" />
                <span className="media-card__vignette" aria-hidden="true" />

                {cue ? (
                    <span className="media-card__cue" aria-hidden="true">
                        {kind === 'project' ? <IconPlay /> : <IconArrowUpRight />}
                    </span>
                ) : null}
            </span>

            <span className="media-card__meta">
                <span className="media-card__head">
                    <span className="media-card__title">{card.title}</span>

                    {area ? (
                        <span className="media-card__area" style={{ '--area': area.color } as React.CSSProperties}>
                            <span className="area-mark" aria-hidden="true" />
                            {area.label}
                        </span>
                    ) : null}
                </span>

                {card.context ? <span className="media-card__context">{card.context}</span> : null}
            </span>
        </>
    );

    const classes = cn(
        'media-card',
        `media-card--${kind}`,
        `media-card--${format}`,
        feature && 'media-card--feature',
        className,
    );

    const preview = videoUrl ? { onMouseEnter: startPreview, onMouseLeave: stopPreview } : undefined;

    // A resolved href wins: it comes from a document's own slug, so there is no
    // nav item to resolve and nothing an editor could have pointed elsewhere.
    // A case has a page of its own to go to, and going there beats a lightbox.
    if (card.href) {
        return (
            <Link href={card.href} className={classes} {...preview}>
                {media}
            </Link>
        );
    }

    // A tile in its section's lightbox group opens there — a film to play, a
    // picture full size — with the group's other tiles one arrow away. The
    // click is picked up by the LightboxTrigger around the section, which holds
    // the slides — this tile only says which one it is.
    if (card.lightboxIndex !== undefined && card.lightboxIndex >= 0 && (videoUrl || src)) {
        return (
            <button
                type="button"
                className={cn(classes, !videoUrl && 'media-card--zoom')}
                data-lightbox-index={card.lightboxIndex}
                aria-label={videoUrl ? undefined : resolveImageActionLabel(card.image, 'vergrößern', card.title)}
                {...preview}
            >
                {media}
            </button>
        );
    }

    // The lightbox is where a film is watched, so a tile opens one when it has
    // a film — which is also what keeps it from ever opening on nothing. Two
    // tiles are excluded even so: a social post lives somewhere else, and the
    // leaving-the-site cue it carries would be a lie if this opened a copy of
    // it here instead.
    if (videoUrl && kind !== 'social' && lightbox) {
        return (
            <button type="button" className={classes} onClick={() => lightbox.open(card, area)} {...preview}>
                {media}
            </button>
        );
    }

    if (!card.link) {
        return (
            <div className={classes} {...preview}>
                {media}
            </div>
        );
    }

    return (
        <BaseLink item={card.link} className={classes} {...preview}>
            {media}
        </BaseLink>
    );
}

export default UiMediaCard;
